<template>
  <div class="benefits-config">
    <div class="header">
      <h3>权益列表配置:</h3>
      <a-button
        type="link"
        @click="addBenefit"
        :disabled="benefits.length >= MAX_BENEFITS"
      >
        <template #icon><PlusOutlined /></template>
        新增项目
      </a-button>
    </div>

    <div v-for="(benefit, index) in benefits" :key="index" class="benefit-item">
      <span class="item-number">{{ index + 1 }}.</span>

      <!-- 图标上传区域 -->
      <div class="icon-uploader-wrapper">
        <a-upload
          :file-list="
            benefit.iconSrc
              ? [
                  {
                    uid: index,
                    name: 'icon.png',
                    status: 'done',
                    url: benefit.iconSrc
                  }
                ]
              : []
          "
          :show-upload-list="false"
          :custom-request="options => customUploadRequest(options, benefit)"
          :before-upload="beforeIconUpload"
          accept="image/*"
          class="icon-uploader"
        >
          <div
            class="upload-area"
            :style="{
              backgroundImage: benefit.iconSrc
                ? `url(${benefit.iconSrc})`
                : 'none'
            }"
          >
            <template v-if="!benefit.iconSrc">
              <PlusOutlined />
            </template>
          </div>
        </a-upload>
        <div
          v-if="benefit.iconSrc"
          class="delete-icon"
          @click="removeIcon(benefit)"
        >
          <CloseOutlined />
        </div>
      </div>

      <!-- 权益文案输入框 -->
      <a-input
        v-model:value="benefit.benefitText"
        placeholder="请输入权益文案"
        class="benefit-input"
        :maxlength="200"
      />

      <!-- 删除按钮 -->
      <a-button
        type="text"
        danger
        @click="deleteBenefit(index)"
        :disabled="benefits.length <= MIN_BENEFITS"
        class="delete-button"
      >
        <DeleteOutlined />
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import {
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { material_upload_post } from '@/server/modules/8945'

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue'])
// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
})

// 常量定义
const MIN_BENEFITS = 2 // 最少2条
const MAX_BENEFITS = 4 // 最多4条

// 权益列表数据
const benefits = ref(props.modelValue || [])

const customUploadRequest = async ({ file }, benefit) => {
  if (file) {
    const { data } = await material_upload_post({ file: file })
    benefit.iconSrc = data.url
  }
}

// 初始化数据，确保至少有 MIN_BENEFITS 条
const initializeBenefits = () => {
  if (benefits.value.length < MIN_BENEFITS) {
    for (let i = 0; i < MIN_BENEFITS - benefits.value.length; i++) {
      benefits.value.push({
        iconSrc: '', // 图标URL
        benefitText: '' // 权益文案
      })
    }
  }
}

// 添加权益项目
const addBenefit = () => {
  if (benefits.value.length < MAX_BENEFITS) {
    benefits.value.push({
      iconSrc: '',
      benefitText: ''
    })
  } else {
    message.warning(`最多只能添加 ${MAX_BENEFITS} 条权益配置项。`)
  }
}

// 删除权益项目
const deleteBenefit = index => {
  if (benefits.value.length > MIN_BENEFITS) {
    benefits.value.splice(index, 1)
  } else {
    message.warning(`最少需要保留 ${MIN_BENEFITS} 条权益配置项。`)
  }
}

// 删除已上传的图标
const removeIcon = benefit => {
  benefit.iconSrc = ''
}

// 上传前的校验
const beforeIconUpload = file => {
  return new Promise((resolve, reject) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      message.error('只能上传 JPG/PNG 格式的图片!')
      return reject(false)
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB!')
      return reject(false)
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const img = new Image()
      img.src = reader.result
      img.onload = () => {
        if (img.width !== img.height) {
          message.error('图片比例必须是 1:1!')
          reject(false)
        } else {
          resolve(true)
        }
      }
    }
  })
}

// 暴露校验方法供父组件调用
// eslint-disable-next-line no-unused-vars
const validate = () => {
  // 当前校验逻辑已移至父组件统兜底校验，保留接口以防将来扩展
}

// eslint-disable-next-line no-undef
defineExpose({
  validate
})

watch(
  () => props.modelValue,
  newVal => {
    benefits.value = newVal || []
    initializeBenefits()
  },
  {
    immediate: true
  }
)

watch(
  () => benefits.value,
  newVal => {
    emit('update:modelValue', newVal)
  },
  {
    deep: true
  }
)
</script>

<style scoped>
.benefits-config {
  width: 100%; /* 调整为适合你页面的宽度 */
  margin: 20px auto;
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.header h3 {
  margin: 0;
  font-size: 14px;
}

.benefit-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #fafafa;
}

.item-number {
  font-weight: bold;
  margin-right: 10px;
  min-width: 20px; /* 确保数字有固定宽度，避免布局抖动 */
  text-align: right;
}

/* 图标上传器样式 */
.icon-uploader-wrapper {
  position: relative;
  display: inline-block;
  margin-right: 10px;
  vertical-align: top; /* 确保对齐 */
}

.icon-uploader {
  display: block;
}

.delete-icon {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 14px;
  height: 14px;
  background-color: rgba(0, 0, 0, 0.45);
  color: #fff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 8px;
  cursor: pointer;
  z-index: 10;
  opacity: 0;
  transition: all 0.3s;
}

.icon-uploader-wrapper:hover .delete-icon {
  opacity: 1;
}

.delete-icon:hover {
  background-color: #ff4d4f;
}

.upload-area {
  width: 32px; /* 略大于22x22，作为点击区域 */
  height: 32px; /* 略大于22x22，作为点击区域 */
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  background-size: cover; /* 确保图片覆盖整个区域 */
  background-position: center; /* 居中显示 */
  background-repeat: no-repeat;
  overflow: hidden; /* 隐藏超出部分 */
  flex-shrink: 0; /* 防止被压缩 */
}

.upload-area:hover {
  border-color: #40a9ff;
}

.upload-area .anticon {
  font-size: 16px; /* 加号图标大小 */
  color: #999;
}

.benefit-input {
  flex-grow: 1; /* 占据剩余空间 */
  margin-right: 10px;
}

.delete-button {
  flex-shrink: 0; /* 防止被压缩 */
  font-size: 16px; /* 删除图标大小 /
  color: #999; / 颜色匹配图像 */
}

.footer {
  text-align: right;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}
</style>
